/** Demo Mode only - EBIT bridge (waterfall) reconciling Budget EBIT -> Current forecast
 * EBIT via named variance categories. Implemented as a stacked BarChart (an invisible
 * "base" series holds each bar off the axis, a visible series draws the actual step) -
 * @mui/x-charts doesn't have a native waterfall chart type, and this avoids pulling in a
 * new chart library for one visual. */

import { BarChart } from '@mui/x-charts/BarChart';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { applyRoundAndFormat } from '../../../utils';

export interface EbitBridgeStep {
  label: string;
  value: number;
}

export interface EbitBridgeChartProps {
  title: string;
  steps: EbitBridgeStep[];
  currencyCode?: string;
  height?: number;
}

const EbitBridgeChart = ({
  title, steps, currencyCode, height = 320,
}: EbitBridgeChartProps) => {
  const { t } = useTranslation('common');
  let running = 0;
  const bases: number[] = [];
  const rises: number[] = [];
  const falls: number[] = [];

  steps.forEach((step, index) => {
    const isEndpoint = index === 0 || index === steps.length - 1;
    if (isEndpoint) {
      bases.push(0);
      rises.push(step.value >= 0 ? step.value : 0);
      falls.push(step.value < 0 ? -step.value : 0);
      running = step.value;
      return;
    }
    if (step.value >= 0) {
      bases.push(running);
      rises.push(step.value);
      falls.push(0);
    } else {
      bases.push(running + step.value);
      rises.push(0);
      falls.push(-step.value);
    }
    running += step.value;
  });

  return (
    <Box>
      <Typography variant="textMD"
        fontWeight={600}
        sx={{ mb: 2 }}>
        {title}
      </Typography>
      <BarChart
        height={height}
        xAxis={[{ scaleType: 'band', data: steps.map((s) => s.label), tickLabelStyle: { fontSize: 10, angle: -20, textAnchor: 'end' } }]}
        series={[
          {
            data: bases, stack: 'bridge', color: 'transparent',
          },
          {
            data: rises, stack: 'bridge', color: '#2e7d32', label: t('financeEbitBridgeIncrease', 'Increase'), valueFormatter: (v) => (v ? applyRoundAndFormat(v, currencyCode) : ''),
          },
          {
            data: falls, stack: 'bridge', color: '#d32f2f', label: t('financeEbitBridgeDecrease', 'Decrease'), valueFormatter: (v) => (v ? applyRoundAndFormat(v, currencyCode) : ''),
          },
        ]}
        slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'right' } } }}
      />
      <Typography variant="textXS"
        color="text.secondary">
        {t('financeEbitBridgeFootnote', 'Endpoints show Budget EBIT and Current forecast EBIT; bars between them show each driver\'s contribution (green = favourable, red = adverse).')}
      </Typography>
    </Box>
  );
};

export default EbitBridgeChart;
